import React, { useState, useEffect } from "react";
import { IComponent } from "@/admin/helpers/interfaces/component-types";
import { siteComponents } from "@/admin/helpers/import/SiteComponents";
import { formatObject } from "@/helpers/helpers";

const INITIAL_COMPONENTS_TO_SHOW = 2;
const LOAD_DELAY_MS = 500000; // Adjust this value as needed

// Define invalid nesting rules
const INVALID_NESTING: { [key: string]: string[] } = {
    'p': ['div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'section']
};

export default function CreatePage(data: IComponent): React.ReactNode {
    const [renderedCount, setRenderedCount] = useState(INITIAL_COMPONENTS_TO_SHOW);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!data) return;

        const totalComponents = countComponents(data);
        if (renderedCount < totalComponents) {
            const timer = setTimeout(() => {
                setIsLoading(false);
                setRenderedCount(totalComponents);
            }, LOAD_DELAY_MS);

            return () => clearTimeout(timer);
        }
    }, [data, renderedCount]);

    if (!data) return null;

    // Helper function to count total components
    function countComponents(component: IComponent): number {
        if (!component) return 0;
        
        let count = 1;
        if (component.childrens) {
            if (Array.isArray(component.childrens)) {
                count += component.childrens.reduce((acc, child) => acc + countComponents(child), 0);
            } else {
                count += countComponents(component.childrens);
            }
        }
        return count;
    }

    function validateNesting(parentType: string, childType: string): boolean {
        if (INVALID_NESTING[parentType]?.includes(childType)) {
            console.warn(`Invalid nesting: <${childType}> cannot be nested inside <${parentType}>`);
            return false;
        }
        return true;
    }

    function createComponent(
        component: IComponent, 
        index?: number, 
        currentDepth: number = 0,
        parentType?: string
    ): React.ReactNode {
        if (!component) return null;

        const { type, props, childrens, id }: any = component;

        // Show loading placeholder for components beyond the initial count
        if (currentDepth >= renderedCount) {
            return (
                <div 
                    key={id || `loading-${index}`}
                    className="component-loading-placeholder"
                    style={{
                        minHeight: '100px',
                        background: '#f0f0f0',
                        margin: '10px 0',
                        borderRadius: '4px',
                        animation: 'pulse 1.5s infinite',
                    }}
                >
                    {isLoading && <div className="loading-indicator">Chargement...</div>}
                </div>
            );
        }

        const enhancedProps = {
            ...formatObject(props),
            key: id || `${type}-${index}`
        };

        // Handle invalid nesting
        if (parentType && !validateNesting(parentType, type)) {
            // If invalid nesting is detected, wrap in a div or adjust the structure
            if (type === 'h6' && parentType === 'p') {
                return (
                    <React.Fragment key={enhancedProps.key}>
                        {React.createElement(
                            'div',
                            { className: 'heading-wrapper' },
                            React.createElement(
                                siteComponents[type],
                                enhancedProps,
                                Array.isArray(childrens) 
                                    ? childrens.map((child, idx) => render(child, `${index}-${idx}`, currentDepth + 1, type))
                                    : render(childrens ?? null, undefined, currentDepth + 1, type)
                            )
                        )}
                    </React.Fragment>
                );
            }
        }

        return React.createElement(
            siteComponents[type],
            enhancedProps,
            Array.isArray(childrens) 
                ? childrens.map((child, idx) => render(child, `${index}-${idx}`, currentDepth + 1, type))
                : render(childrens ?? null, undefined, currentDepth + 1, type)
        );
    }

    function render(
        data: any | null, 
        keyPrefix?: string, 
        depth: number = 0,
        parentType?: string
    ): React.ReactNode {
        if (!data) return null;
        return createComponent(data, keyPrefix, depth, parentType);
    }

    // Add loading styles
    const loadingStyles = `
        @keyframes pulse {
            0% { opacity: 0.6; }
            50% { opacity: 0.8; }
            100% { opacity: 0.6; }
        }
    `;

    return (
        <>
            <style>{loadingStyles}</style>
            {render(data)}
        </>
    );
}